import { useState } from 'react';
import type { ApiClient } from '../../api/client';
import type { IntegrationAccess, IntegrationState } from './context';
import { connectIntegration } from './IntegrationConnections';

export default function IntegrationAccessReview({ request, state, apiClient, refresh }: {
  request: IntegrationAccess; state: IntegrationState; apiClient: ApiClient; refresh: () => Promise<void>;
}) {
  const [selected, setSelected] = useState(request.requested_tools);
  const [minutes, setMinutes] = useState(String(request.requested_ttl_minutes));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const entry = state.available.find(entry => entry.id === request.integration_id);
  const connection = state.connections.find(item => item.integration_id === request.integration_id);
  const pending = request.status === 'pending';
  const expired = Date.parse(pending ? request.request_expires_at : request.expires_at ?? '') <= Date.now();
  const ttl = Number(minutes);
  const connected = connection?.status === 'connected';
  const selectable = request.requested_tools.filter(tool => connection?.tools.some(item => item.name === tool));
  const selectedAvailable = selected.filter(tool => selectable.includes(tool));
  const valid = selectedAvailable.length > 0
    && Number.isSafeInteger(ttl) && ttl >= 1 && ttl <= 1440;
  const answer = async (decision: string) => {
    setBusy(true); setError(null);
    try {
      await apiClient.post('/api/assistant/gateway/integrations/decide', {
        id: request.id, decision, ...(decision === 'approve' ? { tools: selectedAvailable, ttl_minutes: ttl } : {}),
      });
      await refresh();
    } catch { setError('The decision was not confirmed. Refresh to check the saved access.'); }
    finally { setBusy(false); }
  };
  const connect = async () => {
    setBusy(true); setError(null);
    try { await connectIntegration(apiClient, request.integration_id); }
    catch { setError('The app connection is unavailable. Its owner may need to check access policy or OAuth registration.'); setBusy(false); }
  };
  return <article className="assistant-review-card assistant-integration-review">
    <div className="assistant-review-kicker">{pending ? 'App access requested' : expired ? 'App access expired' : request.status === 'approved' ? request.active === false ? 'App access unavailable' : 'App access granted' : 'App access ended'}</div>
    <h3>{entry?.name ?? request.integration_id}</h3><p>{request.purpose}</p>
    {entry && <p className="assistant-review-scope">{entry.capabilities}</p>}
    {pending ? <>
      {!connected && <div className="assistant-integration-connect"><p>Connect your own app account first, then choose what your assistant can use.</p>
        <button disabled={busy || expired} onClick={() => { void connect(); }}>Connect {entry?.name ?? 'app'}</button></div>}
      <fieldset disabled={busy || !connected || expired}><legend>Allow these tools</legend>
        {request.requested_tools.map(tool => <label className="assistant-integration-tool" key={tool}><input type="checkbox" checked={selectable.includes(tool) && selected.includes(tool)}
          disabled={!selectable.includes(tool)} onChange={event => setSelected(current => event.target.checked ? [...current, tool] : current.filter(item => item !== tool))} />
          <span>{tool.replaceAll('_', ' ')}{entry?.write_tools?.includes(tool) ? ' · each change needs review' : ''}{connected && !selectable.includes(tool) ? ' (unavailable)' : ''}</span></label>)}
      </fieldset>
      <label>Allow for (minutes)<input type="number" inputMode="numeric" min="1" max="1440" step="1" value={minutes}
        disabled={busy || expired} onChange={event => setMinutes(event.target.value)} /></label>
      {expired && <p role="status">This request expired. Ask the assistant for a new request.</p>}
      <div className="assistant-review-buttons"><button disabled={busy || !connected || !valid || expired} onClick={() => { void answer('approve'); }}>Allow selected tools</button>
        <button className="ghost" disabled={busy} onClick={() => { void answer('deny'); }}>Not now</button></div>
    </> : <>
      <p className="assistant-review-scope">{request.approved_tools?.map(tool => tool.replaceAll('_', ' ')).join(', ')}</p>
      {request.expires_at && <p>Expires {new Date(request.expires_at).toLocaleString()}</p>}
      {request.status === 'approved' && <button className="ghost" disabled={busy} onClick={() => { void answer('revoke'); }}>Revoke app access</button>}
    </>}
    {error && <p role="alert">{error}</p>}
  </article>;
}
